/**
 * MatlabImportPanel — paste MS's `.m` file, see what it changes, confirm.
 *
 * The flow's numbers are HIS: weights, filters, deratings, pack efficiencies,
 * the series rounding, the energy basis. Until now they reached the product by
 * retyping, and nothing noticed when that did not happen — the live eBike flow
 * ranked on 10 Wh/km where his script says 15. This panel is the way back to one
 * source: he edits his script, it lands here, the diff says what moves.
 *
 * THREE STEPS, AND THE MIDDLE ONE IS THE POINT. Preview reads and reports;
 * nothing is written until "Apply". The diff is what an admin actually reviews —
 * a one-line change should look like one line, which is why provenance
 * (hash, date) is attached server-side AFTER the diff is computed and never
 * appears in it.
 *
 * A script the importer cannot read produces an error naming the symbol it did
 * not understand, and nothing is imported. That refusal is the feature: a
 * partial import leaves a flow that looks configured and is not.
 *
 * Applying goes through the ordinary flow update (`updateFlow`), so a knowledge
 * expert's import queues for approval exactly like a hand edit. There is no
 * import-specific write path to forget the gate in.
 */
import { useState } from 'preact/hooks'

import { previewMatlabImport, updateFlow } from '../../../services/flowsService.js'

/** `defaults.energy_per_distance_wh_km` → `defaults › energy per distance wh km` */
function readablePath(path) {
  return path
    .split('.')
    .map((part) => part.replace(/_/g, ' '))
    .join(' › ')
}

/** Render a value the way an admin reads it, not the way JSON serialises it. */
function readableValue(value) {
  if (value === null || value === undefined) return '—'
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'object') {
    // A constraint like {op: '<=', value: 8} reads as "<= 8".
    if ('op' in value && 'value' in value) return `${value.op} ${value.value}`
    return Object.entries(value)
      .map(([k, v]) => `${k.replace(/_/g, ' ')} ${v}`)
      .join(' · ')
  }
  return String(value)
}

export function MatlabImportPanel({ slug, onImported }) {
  const [script, setScript] = useState('')
  const [filename, setFilename] = useState('')
  const [preview, setPreview] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [applied, setApplied] = useState(null)

  const canPreview = script.trim().length > 20 && !busy

  async function handlePreview() {
    setBusy(true)
    setError(null)
    setApplied(null)
    try {
      setPreview(await previewMatlabImport(slug, { script, filename }))
    } catch (e) {
      setPreview(null)
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  async function handleApply() {
    setBusy(true)
    setError(null)
    try {
      const saved = await updateFlow(slug, { spec: preview.spec })
      setApplied(saved?.queued ? 'queued' : 'saved')
      setPreview(null)
      setScript('')
      onImported?.(saved)
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const blocked = preview && preview.errors?.length > 0

  return (
    <section className="admin-aim-section admin-flow-section">
      <div className="admin-flow-section__head">
        <h3 className="admin-aim-section-title">Import from MATLAB</h3>
      </div>
      <p className="admin-flow-hint">
        Paste the whole <code>.m</code> file. The import reads the blocks the
        script marks itself (<code>USER INPUTS</code>, <code>ASSUMPTIONS</code>)
        plus the ranking, and leaves the wording of the flow — intro, questions,
        report — alone. Anything it does not recognise stops the import rather
        than being skipped.
      </p>

      <input
        className="admin-aim-input"
        type="text"
        placeholder="File name (optional, e.g. ebike_cell_selection.m)"
        value={filename}
        onInput={(e) => setFilename(e.currentTarget.value)}
      />
      <textarea
        className="admin-aim-input admin-flow-textarea"
        rows={8}
        placeholder="% --- USER INPUTS SECTION 1 --- …"
        value={script}
        aria-label="MATLAB script"
        onInput={(e) => {
          setScript(e.currentTarget.value)
          setPreview(null)
          setApplied(null)
        }}
      />

      <div className="admin-flow-weights-actions">
        <button
          type="button"
          className="admin-ai-btn"
          disabled={!canPreview}
          onClick={handlePreview}
        >
          {busy && !preview ? 'Reading…' : 'Preview changes'}
        </button>
        {preview && (
          <button
            type="button"
            className="admin-ai-btn admin-ai-btn--primary"
            disabled={busy || blocked}
            onClick={handleApply}
            title={
              blocked
                ? 'Fix the errors below first — this spec would not save.'
                : undefined
            }
          >
            {/* An empty diff is NOT a no-op: applying also records which keys
                the script owns (`matlab_source.owns`), and the diff deliberately
                leaves provenance out so a hash and a date cannot bury the two
                lines that change the ranking. Saying "Apply 0 changes" made the
                one useful action look pointless. */}
            {preview.changes.length === 0
              ? 'Herkunft aktualisieren'
              : `Apply ${preview.changes.length} change${
                  preview.changes.length === 1 ? '' : 's'
                }`}
          </button>
        )}
      </div>

      {error && (
        <p className="admin-flow-inline-error" role="alert">
          {error}
        </p>
      )}

      {applied && (
        <p className="admin-flow-hint" role="status">
          {applied === 'queued'
            ? 'Queued for approval — the live flow is unchanged until an approver publishes it.'
            : 'Imported. The flow is live on the next chat turn.'}
        </p>
      )}

      {preview && (
        <div className="admin-flow-import-preview">
          {preview.changes.length === 0 ? (
            <p className="admin-aim-empty admin-flow-empty">
              Nothing changes — this flow already matches the script. Applying
              still refreshes the provenance (file, checksum, date, and which
              keys the script owns).
            </p>
          ) : (
            <table className="admin-flow-diff">
              <thead>
                <tr>
                  <th scope="col">Field</th>
                  <th scope="col">Now</th>
                  <th scope="col">After import</th>
                </tr>
              </thead>
              <tbody>
                {preview.changes.map((c) => (
                  <tr key={c.path}>
                    <td>{readablePath(c.path)}</td>
                    <td className="admin-flow-diff__before">
                      {readableValue(c.before)}
                    </td>
                    <td className="admin-flow-diff__after">
                      {readableValue(c.after)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {preview.errors?.length > 0 && (
            <ul className="admin-flow-errors" role="alert">
              {preview.errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
          {preview.warnings?.length > 0 && (
            <ul className="admin-flow-warnings">
              {preview.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}
